import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';

/**
 * Gantt-style draggable leave duration picker.
 *
 *   Flow:
 *     1. User enters Start date and Working days.
 *     2. The pink range box appears, computed by walking forward from Start
 *        and counting `working_days` working days (weekends + holidays skipped).
 *     3. User can then drag the box to shift dates, or grab either handle
 *        to resize — working days auto-recomputed.
 *
 *   Props:
 *     fromDate / toDate   — ISO yyyy-mm-dd strings (controlled)
 *     onChange({from_date, to_date, working_days, calendar_days})
 *     holidays            — [{ holiday_date, name, category }]
 *     shift               — { working_days_mask } (62 = Mon-Fri default)
 */
export default function GanttLeavePicker({
  fromDate, toDate, onChange, holidays = [], shift = null,
}) {
  const mask = shift && typeof shift.working_days_mask === 'number' ? shift.working_days_mask : 62;
  function isWorkingDay(date) {
    const dow = date.getDay();
    const bit = dow === 0 ? 64 : 1 << (dow - 1); // Mon=1..Sun=64
    return (mask & bit) !== 0;
  }
  const holidaySet = useMemo(() => {
    const s = new Set();
    for (const h of holidays) if (h && h.holiday_date) s.add(String(h.holiday_date).slice(0, 10));
    return s;
  }, [holidays]);
  const holidayMap = useMemo(() => {
    const m = {};
    for (const h of holidays) if (h && h.holiday_date) m[String(h.holiday_date).slice(0, 10)] = h;
    return m;
  }, [holidays]);

  /* ─── Input state: start + working days ─── */
  const [startInput, setStartInput] = useState(fromDate || '');
  const [daysInput, setDaysInput] = useState(() => {
    if (fromDate && toDate) return String(countWorkingDaysBetween(fromDate, toDate, isWorkingDay, holidaySet));
    return '';
  });

  function walkForward(startISO, workingDays) {
    if (!startISO || !workingDays || workingDays < 1) return startISO;
    let used = 0;
    const d = new Date(startISO);
    if (isWorkingDay(d) && !holidaySet.has(isoOf(d))) used = 1;
    const startTs = d.getTime();
    while (used < workingDays) {
      d.setDate(d.getDate() + 1);
      if (isWorkingDay(d) && !holidaySet.has(isoOf(d))) used++;
      if (d.getTime() - startTs > 365 * 86400000) break;
    }
    return isoOf(d);
  }

  function applyInputs(nextStart, nextDays) {
    if (!nextStart || !nextDays || +nextDays < 1) {
      onChange && onChange({ from_date: nextStart || '', to_date: '', working_days: 0, calendar_days: 0 });
      return;
    }
    const to = walkForward(nextStart, parseInt(nextDays, 10));
    const calDays = Math.round((new Date(to) - new Date(nextStart)) / 86400000) + 1;
    onChange && onChange({
      from_date: nextStart, to_date: to,
      working_days: parseInt(nextDays, 10),
      calendar_days: calDays,
    });
  }

  useEffect(() => { setStartInput(fromDate || ''); }, [fromDate]);
  useEffect(() => {
    if (fromDate && toDate) setDaysInput(String(countWorkingDaysBetween(fromDate, toDate, isWorkingDay, holidaySet)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate, mask, holidaySet]);

  /* ─── Continuous multi-month strip ─────────────────────────────────
   *
   *   Base date = first of the month of fromDate (or current month).
   *   We render 180 days of cells at fixed pixel width (CELL_W).
   *   The pink box is drawn over them using (date - base) day offsets,
   *   so ranges spanning multiple months are a single continuous bar.
   *
   *   The strip scrolls horizontally (overflow-x:auto) and we auto-scroll
   *   to keep the start of the range in view when the user changes inputs.
   * ─────────────────────────────────────────────────────────────────── */
  const CELL_W = 44;          // px per day
  const STRIP_DAYS = 180;     // ~6 months visible via scroll
  const baseDate = useMemo(() => {
    const d = fromDate ? new Date(fromDate) : new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  }, [fromDate]);

  /** Offset in days from baseDate. Returns null if date is outside the strip. */
  function dayOffsetOf(iso) {
    if (!iso) return null;
    const d = new Date(iso);
    const delta = Math.round((d.getTime() - baseDate.getTime()) / 86400000);
    if (delta < 0 || delta >= STRIP_DAYS) return null;
    return delta;
  }
  function isoAtOffset(o) {
    const d = new Date(baseDate);
    d.setDate(baseDate.getDate() + o);
    return isoOf(d);
  }

  function countWorkingDaysLocal(s, e) {
    if (s == null || e == null || e < s) return 0;
    let n = 0;
    for (let i = s; i <= e; i++) {
      const d = new Date(baseDate); d.setDate(baseDate.getDate() + i);
      if (isWorkingDay(d) && !holidaySet.has(isoOf(d))) n++;
    }
    return n;
  }

  function fireChange(s, e) {
    if (s == null || e == null || s > e) return;
    onChange && onChange({
      from_date: isoAtOffset(s),
      to_date:   isoAtOffset(e),
      working_days: countWorkingDaysLocal(s, e),
      calendar_days: e - s + 1,
    });
  }

  const startOffset = dayOffsetOf(fromDate);
  const endOffset   = dayOffsetOf(toDate);
  const hasRange = startOffset != null && endOffset != null;
  const workingDays = hasRange ? countWorkingDaysLocal(startOffset, endOffset) : 0;

  /** Month band — compute the set of month boundaries visible in the strip. */
  const monthBands = useMemo(() => {
    const bands = [];
    let o = 0;
    while (o < STRIP_DAYS) {
      const d = new Date(baseDate); d.setDate(baseDate.getDate() + o);
      const mEnd = new Date(d.getFullYear(), d.getMonth() + 1, 0); // last day of this month
      const endOff = Math.min(STRIP_DAYS - 1, o + (mEnd.getDate() - d.getDate()));
      bands.push({
        startOffset: o,
        endOffset: endOff,
        label: d.toLocaleDateString('en-IN', { month: 'short', year: '2-digit' }),
      });
      o = endOff + 1;
    }
    return bands;
  }, [baseDate]);

  /* ─── Scroll the strip so the range is visible ─── */
  const scrollerRef = useRef(null);
  useEffect(() => {
    if (!scrollerRef.current || startOffset == null) return;
    const targetX = Math.max(0, startOffset * CELL_W - 40);
    scrollerRef.current.scrollTo({ left: targetX, behavior: 'smooth' });
  }, [startOffset]);

  /* ─── Drag ─── */
  const dragRef = useRef(null);
  function onMouseDown(ev, mode) {
    ev.preventDefault();
    dragRef.current = { mode, startX: ev.clientX, startS: startOffset, startE: endOffset };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }
  const onMouseMove = useCallback((ev) => {
    if (!dragRef.current) return;
    const delta = Math.round((ev.clientX - dragRef.current.startX) / CELL_W);
    let s = dragRef.current.startS ?? 0;
    let e = dragRef.current.startE ?? 0;
    if (dragRef.current.mode === 'move') {
      const len = e - s;
      s = Math.max(0, Math.min(STRIP_DAYS - 1 - len, s + delta));
      e = s + len;
    } else if (dragRef.current.mode === 'resize-l') {
      s = Math.max(0, Math.min(e, s + delta));
    } else if (dragRef.current.mode === 'resize-r') {
      e = Math.max(s, Math.min(STRIP_DAYS - 1, e + delta));
    }
    fireChange(s, e);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseDate, mask, holidaySet]);
  const onMouseUp = useCallback(() => {
    dragRef.current = null;
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('mouseup', onMouseUp);
  }, [onMouseMove]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* ── Input row ── */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
        <label style={labelStack}>
          <span style={labelCap}>Start date</span>
          <input
            type="date"
            value={startInput}
            onChange={e => { const v = e.target.value; setStartInput(v); applyInputs(v, daysInput); }}
            style={{ ...inputStyle, width: 170 }}
          />
        </label>
        <label style={labelStack}>
          <span style={labelCap}>Working days</span>
          <input
            type="number" min="1" max="180" value={daysInput}
            onChange={e => { const v = e.target.value; setDaysInput(v); applyInputs(startInput, v); }}
            style={{ ...inputStyle, width: 100 }}
            placeholder="e.g. 5"
          />
        </label>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, color: '#64748B' }}>
            {fromDate && toDate ? (
              <>→ ends <strong style={{ color: '#E5E7EB' }}>{fmtDate(toDate)}</strong>
                {' · '}<span style={{ color: '#10B981' }}>{workingDays} working</span>
                {' / '}<span>{endOffset - startOffset + 1} calendar</span>
              </>
            ) : (
              <span>Enter a start date and number of working days — weekends &amp; holidays are skipped automatically.</span>
            )}
          </div>
        </div>
      </div>

      <div style={{ fontSize: 10.5, color: '#64748B', textAlign: 'right' }}>
        {hasRange ? 'Drag the pink bar to shift · grab handles to resize · scroll horizontally to see more' : 'Pink bar appears once you pick a start & days'}
      </div>

      {/* ── Scrollable continuous strip ── */}
      <div
        ref={scrollerRef}
        style={{
          position: 'relative',
          overflowX: 'auto', overflowY: 'hidden',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 10,
          background: 'linear-gradient(90deg, rgba(59,143,244,0.04), rgba(191,95,255,0.04))',
        }}
      >
        <div style={{ width: STRIP_DAYS * CELL_W, position: 'relative' }}>
          {/* Month label band */}
          <div style={{ position: 'relative', height: 22, borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
            {monthBands.map((b, i) => (
              <div
                key={i}
                style={{
                  position: 'absolute',
                  left: b.startOffset * CELL_W,
                  width: (b.endOffset - b.startOffset + 1) * CELL_W,
                  top: 0, bottom: 0,
                  borderRight: '1px solid rgba(255,255,255,0.08)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 10.5, fontWeight: 700, letterSpacing: '0.5px',
                  color: '#94A3B8', textTransform: 'uppercase',
                  background: i % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent',
                }}
              >
                {b.label}
              </div>
            ))}
          </div>

          {/* Day cells + leave box */}
          <div style={{ position: 'relative', height: 64 }}>
            {Array.from({ length: STRIP_DAYS }, (_, i) => {
              const d = new Date(baseDate); d.setDate(baseDate.getDate() + i);
              const iso = isoOf(d);
              const isWeekend = !isWorkingDay(d);
              const hol = holidayMap[iso];
              const today = new Date();
              const isToday = today.getFullYear() === d.getFullYear() && today.getMonth() === d.getMonth() && today.getDate() === d.getDate();
              const wd = d.toLocaleDateString('en-IN', { weekday: 'short' });
              const isMonthStart = d.getDate() === 1;
              return (
                <div
                  key={i}
                  style={{
                    position: 'absolute', top: 0, bottom: 0,
                    left: i * CELL_W, width: CELL_W,
                    borderRight: isMonthStart ? '1px solid rgba(255,255,255,0.12)' : '1px dashed rgba(255,255,255,0.04)',
                    background: hol ? 'rgba(239,68,68,0.10)' : (isWeekend ? 'rgba(255,255,255,0.03)' : 'transparent'),
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'space-between',
                    padding: '8px 0',
                    color: hol ? '#FCA5A5' : (isWeekend ? '#475569' : '#94A3B8'),
                    fontSize: 10, pointerEvents: 'none', zIndex: 1,
                    boxShadow: isToday ? 'inset 0 0 0 1px rgba(0,224,255,0.4)' : 'none',
                  }}
                  title={hol ? `${hol.name} (${hol.category})` : d.toLocaleDateString('en-IN', { dateStyle: 'full' })}
                >
                  <div style={{ fontSize: 11, fontWeight: 600 }}>{d.getDate()}</div>
                  <div style={{ fontSize: 8.5, letterSpacing: '0.3px', textTransform: 'uppercase' }}>{wd.slice(0, 2)}</div>
                </div>
              );
            })}

            {hasRange && (
              <div
                onMouseDown={ev => onMouseDown(ev, 'move')}
                style={{
                  position: 'absolute',
                  left: startOffset * CELL_W + 2,
                  width: (endOffset - startOffset + 1) * CELL_W - 4,
                  top: 4, bottom: 4,
                  background: 'linear-gradient(135deg, rgba(191,95,255,0.35), rgba(255,0,229,0.35))',
                  border: '1px solid rgba(255,0,229,0.65)',
                  borderRadius: 7,
                  cursor: 'grab',
                  zIndex: 5,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 700, color: '#fff',
                  boxShadow: '0 6px 20px rgba(255,0,229,0.25)',
                  userSelect: 'none',
                }}
              >
                <div
                  onMouseDown={ev => { ev.stopPropagation(); onMouseDown(ev, 'resize-l'); }}
                  style={{
                    position: 'absolute', left: 0, top: 0, bottom: 0, width: 8,
                    cursor: 'ew-resize', borderRight: '1px solid rgba(255,255,255,0.4)',
                    borderTopLeftRadius: 7, borderBottomLeftRadius: 7,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <div style={{ width: 2, height: 14, background: 'rgba(255,255,255,0.7)', borderRadius: 1 }} />
                </div>
                <div style={{ pointerEvents: 'none' }}>{workingDays} day{workingDays === 1 ? '' : 's'}</div>
                <div
                  onMouseDown={ev => { ev.stopPropagation(); onMouseDown(ev, 'resize-r'); }}
                  style={{
                    position: 'absolute', right: 0, top: 0, bottom: 0, width: 8,
                    cursor: 'ew-resize', borderLeft: '1px solid rgba(255,255,255,0.4)',
                    borderTopRightRadius: 7, borderBottomRightRadius: 7,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <div style={{ width: 2, height: 14, background: 'rgba(255,255,255,0.7)', borderRadius: 1 }} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Legend ── */}
      <div style={{ display: 'flex', gap: 14, fontSize: 11, color: '#64748B', flexWrap: 'wrap' }}>
        <span><span style={sw('rgba(255,0,229,0.5)')} />Leave range</span>
        <span><span style={sw('rgba(255,255,255,0.06)')} />Weekly off</span>
        <span><span style={sw('rgba(239,68,68,0.20)')} />Holiday</span>
        <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, boxShadow: 'inset 0 0 0 1px rgba(0,224,255,0.6)', marginRight: 5, verticalAlign: 'middle' }} />Today</span>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
 * MobileLeavePicker — Option B (month-grid click-pick) for narrow screens
 * ══════════════════════════════════════════════════════════════════════ */
export function MobileLeavePicker({ fromDate, toDate, onChange, holidays = [], shift = null }) {
  const [anchor, setAnchor] = useState(() => {
    const base = fromDate ? new Date(fromDate) : new Date();
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });

  const mask = shift && typeof shift.working_days_mask === 'number' ? shift.working_days_mask : 62;
  function isWorkingDay(d) {
    const dow = d.getDay();
    const bit = dow === 0 ? 64 : 1 << (dow - 1);
    return (mask & bit) !== 0;
  }
  const holidaySet = useMemo(() => {
    const s = new Set();
    for (const h of holidays) if (h.holiday_date) s.add(String(h.holiday_date).slice(0, 10));
    return s;
  }, [holidays]);
  const holidayMap = useMemo(() => {
    const m = {};
    for (const h of holidays) if (h.holiday_date) m[String(h.holiday_date).slice(0, 10)] = h;
    return m;
  }, [holidays]);

  const daysInMonth = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0).getDate();
  const firstDow = (new Date(anchor.getFullYear(), anchor.getMonth(), 1).getDay() + 6) % 7;
  const fromD = fromDate ? new Date(fromDate) : null;
  const toD   = toDate   ? new Date(toDate)   : null;

  function inRange(d) {
    if (!fromD || !toD) return false;
    const t = d.getTime();
    return t >= fromD.getTime() && t <= toD.getTime();
  }

  function onCellTap(d) {
    if (!fromD || (fromD && toD)) {
      const i = isoOf(d);
      onChange && onChange({ from_date: i, to_date: i, working_days: isWorkingDay(d) && !holidaySet.has(i) ? 1 : 0, calendar_days: 1 });
      return;
    }
    let s = fromD, e = d;
    if (e < s) { const tmp = s; s = e; e = tmp; }
    let working = 0;
    for (let cur = new Date(s); cur <= e; cur.setDate(cur.getDate() + 1)) {
      if (isWorkingDay(cur) && !holidaySet.has(isoOf(cur))) working++;
    }
    onChange && onChange({
      from_date: isoOf(s), to_date: isoOf(e),
      working_days: working,
      calendar_days: Math.round((e - s) / 86400000) + 1,
    });
  }

  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(anchor.getFullYear(), anchor.getMonth(), d));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <button type="button" onClick={() => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1))} style={navBtn}>◀</button>
        <div style={{ fontSize: 13, fontWeight: 600 }}>{anchor.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}</div>
        <button type="button" onClick={() => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1))} style={navBtn}>▶</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3 }}>
        {['M','T','W','T','F','S','S'].map((l, i) => (
          <div key={i} style={{ fontSize: 10, color: '#64748B', textAlign: 'center', padding: '4px 0', textTransform: 'uppercase' }}>{l}</div>
        ))}
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const wd = !isWorkingDay(d);
          const hol = holidayMap[isoOf(d)];
          const on = inRange(d);
          const start = fromD && d.getTime() === fromD.getTime();
          const end   = toD   && d.getTime() === toD.getTime();
          return (
            <button
              key={i} type="button" onClick={() => onCellTap(d)}
              style={{
                aspectRatio: '1.2', borderRadius: 6, cursor: 'pointer',
                background: on
                  ? 'linear-gradient(135deg, rgba(191,95,255,0.35), rgba(255,0,229,0.35))'
                  : hol ? 'rgba(239,68,68,0.10)' : wd ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.04)',
                border: '1px solid ' + (on ? 'rgba(255,0,229,0.6)' : 'rgba(255,255,255,0.08)'),
                color: on ? '#fff' : hol ? '#FCA5A5' : wd ? '#475569' : '#E5E7EB',
                fontSize: 12, fontWeight: on ? 700 : 500,
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', padding: '3px 0',
                ...(start ? { borderLeftWidth: 3 } : {}),
                ...(end   ? { borderRightWidth: 3 } : {}),
              }}
              title={hol ? hol.name : ''}
            >
              <div>{d.getDate()}</div>
              {hol && <div style={{ fontSize: 8, marginTop: 1 }}>•</div>}
            </button>
          );
        })}
      </div>

      <div style={{ display: 'flex', gap: 12, fontSize: 11.5, color: '#94A3B8', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <div><span style={{ color: '#64748B' }}>From:</span> <strong style={{ color: '#E5E7EB' }}>{fmtDate(fromDate)}</strong></div>
        <div><span style={{ color: '#64748B' }}>To:</span> <strong style={{ color: '#E5E7EB' }}>{fmtDate(toDate)}</strong></div>
      </div>
      <div style={{ fontSize: 10.5, color: '#64748B', textAlign: 'center' }}>Tap a date, then tap the end date to pick a range.</div>
    </div>
  );
}

/* Desktop ≥ 768px → Gantt; else mobile grid. */
export function ResponsiveLeavePicker(props) {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  useEffect(() => {
    function onResize() { setIsMobile(window.innerWidth < 768); }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isMobile ? <MobileLeavePicker {...props} /> : <GanttLeavePicker {...props} />;
}

/* ── helpers ── */
function isoOf(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function fmtDate(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
}
function countWorkingDaysBetween(fromISO, toISO, isWorkingFn, holidaySet) {
  const s = new Date(fromISO), e = new Date(toISO);
  let n = 0;
  for (let cur = new Date(s); cur <= e; cur.setDate(cur.getDate() + 1)) {
    if (isWorkingFn(cur) && !holidaySet.has(isoOf(cur))) n++;
  }
  return n;
}
function sw(bg) { return { display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: bg, marginRight: 5, verticalAlign: 'middle' }; }

const navBtn = {
  width: 28, height: 28, padding: 0, borderRadius: 6,
  border: '1px solid rgba(255,255,255,0.10)',
  background: 'rgba(255,255,255,0.04)', color: '#E5E7EB',
  cursor: 'pointer', fontSize: 11,
};
const inputStyle = {
  padding: '8px 10px', borderRadius: 7,
  border: '1px solid rgba(255,255,255,0.10)',
  background: 'rgba(255,255,255,0.04)', color: '#E5E7EB',
  fontSize: 13, outline: 'none',
};
const labelStack = { display: 'flex', flexDirection: 'column', gap: 4 };
const labelCap = { fontSize: 10.5, color: '#94A3B8', letterSpacing: '0.4px', textTransform: 'uppercase', fontWeight: 600 };
